import React from 'react';
import { connect } from 'react-redux';
import { BrowserRouter as Router, Switch, Route, Redirect } from 'react-router-dom';
import { lifecycle } from 'recompose';
import { default as configs } from './configs';
import styles from './app.module.css';
import { getToken } from '../reducers/auth';
import { logger } from './helpers';

import Header from './common/components/header';
import Footer from './common/components/footer';
import MainPage from './common/components/main-page';
import ProgressBar from './common/components/progress-bar';
import Pagination from './common/components/pagination';
import Tips from './common/components/tips';
import Home from './common/containers/home';
import DebugPanel from './common/components/debug-panel';



const appNavigation = Object
    .values(configs)
    .map(({ name, redirectFromSteps = false, inNavigation = false }, i) => ({
        name,
        path: Object.keys(configs)[i],
        redirectFromSteps,
        inNavigation
    }))
    .filter(({inNavigation = false}) => inNavigation === true);

const appIndex = Object
    .values(configs)
    .map(({ name, redirectFromSteps = false }, i) => ({
        name,
        path: Object.keys(configs)[i],
        redirectFromSteps
    }));

const secondaryMenu = Object
    .values(configs)
    .map(({ name, redirectFromSteps = false, inSecondaryMenu = false }, i) => ({
        name,
        path: Object.keys(configs)[i],
        routes: Object.values(configs)[i].routes,
        redirectFromSteps,
        inSecondaryMenu
    }))
    .filter(({inSecondaryMenu = false}) => inSecondaryMenu === true);

// Every unmatched route falls back here. PUBLIC_URL has no trailing slash
// (CRA strips it), so this is '/' in development and '/calculators/' in
// the deployed builds.
const homePath = `${process.env.PUBLIC_URL}/`;

// Sends the visitor home and hands the homepage the path they asked for, so
// it can tell them why they ended up somewhere else.
const redirectHome = ({ location }) => (
    <Redirect
        to={{
            pathname: homePath,
            state: { invalidPath: `${location.pathname}${location.search}` }
        }}
    />
);

const App = ({
    pagination
}) => (
    <div className={pagination ? styles.appPagination : styles.app}>
        <Header navigation={appNavigation}/>
        <Router>
            <MainPage navigationIndex={appIndex}>
                <Switch>
                    {
                        Object.keys(configs).map(appRoute =>
                            <Route
                                path={`${process.env.PUBLIC_URL}${appRoute}`}
                                key={appRoute}
                                component={() => [
                                    <ProgressBar
                                        key='progress'
                                        routes={configs[appRoute].routes}
                                    />,
                                    <Switch key='calculator-routes'>
                                        {
                                            configs[appRoute].routes.map(({ path, component }) =>
                                                <Route
                                                    key={`${process.env.PUBLIC_URL}${appRoute}${path}`}
                                                    path={`${process.env.PUBLIC_URL}${appRoute}${path}`}
                                                    exact={true}
                                                    component={component}
                                                />
                                            )
                                        }
                                        {/* Unknown step within a known calculator (and any
                                            calculator without its own index route) goes home. */}
                                        <Route render={redirectHome}/>
                                    </Switch>,
                                    <Pagination
                                        key='pagination'
                                        routes={configs[appRoute].routes}
                                    />,
                                    <Tips
                                        key='tips'
                                        routes={configs[appRoute].routes}
                                    />
                                ]}
                            >
                            </Route>
                        )
                    }
                    <Route
                        exact={true}
                        path={homePath}
                        render={() => <Home navigation={appNavigation} secondaryMenu={secondaryMenu}/>}
                    />
                    {/* Any route outside a known calculator goes home. */}
                    <Route render={redirectHome}/>
                </Switch>
            </MainPage>
        </Router>
        <Footer pagination={pagination}/>
        {/* Debug Panel - only show when debug is enabled or in development */}
        {(process.env.NODE_ENV === 'development' || localStorage.getItem('libro_debug_enabled') === 'true') && (
            <DebugPanel />
        )}
    </div>
);

const AppWithLifecycle = lifecycle({
    async componentDidMount () {
        const {
            props: {
                dispatch
            }
        } = this;

        try {
            await dispatch(getToken());
        } catch (error) {
            logger.error('Failed to initialize app or retrieve authentication token', {
                error: error.message,
                stack: error.stack
            });
        }
    }
})(App);

const mapStateToProps = ({ calculator }) => ({
    pagination: calculator.pagination
});

export default connect(
    mapStateToProps
)(AppWithLifecycle);
